import React, { useEffect, useState } from 'react';
import DeleteIcon from 'assets/Icons/delete/DeleteIcons';
import BackIcon from 'assets/Icons/modals/BackIcon';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import Modal from 'components/elements/Modal';
import useDeleteOccupant from './crud/useDelete';
import { toast } from 'react-toastify';
import {
  generateApiFailErrorMessage,
  generateDeleteErrorMessage,
  generateDeleteSuccessMessage
} from 'utils/helpers';
import { useQuery } from 'react-query';
import { OccupantProps } from 'services/hooks/occupant-service';
import { CardFormat } from 'utils/constants';
import InfoBox from 'utils/InfoBox';
import { CardProps } from 'services/hooks/card-service';

type DeleteProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleteUUID: React.Dispatch<React.SetStateAction<string>>;
  deleteUUID?: string;
  deleteQuery: any;
  editQuery?: any;
  queryName: string;
};

export default function Delete({
  entityName,
  showModal,
  setShowModal,
  setDeleteUUID,
  deleteUUID,
  deleteQuery,
  editQuery,
  queryName
}: DeleteProps) {
  const [ictCardExists, setIctCardExists] = useState(false);

  const { data: OccupantInfo } = useQuery<OccupantProps, Error>(
    'occupant',
    () => editQuery(deleteUUID),
    {
      enabled: !!deleteUUID,
      cacheTime: 0,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Occupant'));
      }
    }
  );

  useEffect(() => {
    if (OccupantInfo?.cards) {
      const ictFormatCardExists = OccupantInfo.cards.some(
        (card: CardProps) => card.format === CardFormat.ICT
      );
      setIctCardExists(ictFormatCardExists);
    }
  }, [OccupantInfo]);

  const deleteOccupantMutation = useDeleteOccupant({
    deleteQuery,
    queryName,
    removeOnSuccess: true
  });

  const handleDelete = () => {
    setShowModal(false);
    toast
      .promise(
        // Call the mutate function by passing in the necessary arguments
        () => deleteOccupantMutation.mutateAsync(deleteUUID),
        {
          pending: 'Deleting Occupant...',
          success: generateDeleteSuccessMessage('Occupant'),
          error: generateDeleteErrorMessage('Occupant')
        }
      )
      .then(() => {
        // Handling after success
        setDeleteUUID('');
      })
      .catch(() => {
        // Handling after error
        setDeleteUUID('');
      });
  };

  return (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setDeleteUUID) setDeleteUUID('');
        }}
      >
        <div className="flex flex-col items-center">
          <div className="delete-icon">
            <DeleteIcon />
          </div>
          <h1 className="mt-4">Are you sure?</h1>
          {ictCardExists && (
            <InfoBox
              message={`This occupant has an assigned ICT card. Deleting the occupant will also delete the card. If the card has been consumed (activated in the mobile app), deleting it will permanently revoke access, making the card unusable. This action is irreversible.`}
              sx={{ textAlign: 'center' }}
            />
          )}

          <p>{`Are you sure you want to delete this ${entityName.toLowerCase()}?`}</p>
          <div className="flex justify-between space-x-2">
            <SecondaryButton
              buttonLabel="Back"
              iconLeft={<BackIcon />}
              variant="default"
              size="px-4 w-full"
              onClick={() => {
                setShowModal(false);
                setDeleteUUID('');
              }}
            />
            <PrimaryButton
              buttonLabel="Delete"
              onClick={handleDelete}
              variant="default"
              size="px-4 w-auto gap-2"
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
